import React, { useState, useEffect } from "react";
import BaseModal from "components/BaseModal";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

export default function UserBasedOperatorFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors]             = useState({});
  const [providerList, setProviderList] = useState([]);
  const [userList, setUserList]         = useState([]);
  const [editData, setEditData]         = useState(null);   // ✅ holds GETBYID response
  const { userid } = useSession();

  // ✅ Fetch dropdowns
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const provForm = new FormData();
        provForm.append("action", "GET");
        provForm.append("userid", userid);
        const provRes = await fetch("https://pro.mokshiq.in/provider_manager_action", {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body: provForm,
        });
        const provData = await provRes.json();
        setProviderList(provData.data || []);

        const userForm = new FormData();
        userForm.append("action", "GET");
        userForm.append("userid", userid);
        const userRes = await fetch("https://pro.mokshiq.in/retailer_action", {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body: userForm,
        });
        const userData2 = await userRes.json();
        setUserList(userData2.data || []);
      } catch (err) {
        console.error("Error fetching dropdowns:", err);
      }
    };
    fetchDropdowns();
  }, []);

  // ✅ Fetch record by ID when editing
  useEffect(() => {
    if (mode === "edit" && userData?.id) {
      const fetchById = async () => {
        try {
          const form = new FormData();
          form.append("action", "GETBYID");
          form.append("id", userData.id);
          form.append("userid", userid);
          const res = await fetch("https://pro.mokshiq.in/user_based_operator_action", {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            body: form,
          });
          const result = await res.json();
          if (result.status) {
            setEditData(result.data);  // ✅ has provider_id, user_id, min_amt, max_amt, optional
          }
        } catch (err) {
          console.error("Error fetching record:", err);
        }
      };
      fetchById();
    } else {
      setEditData(null);  // ✅ reset on add mode
    }
  }, [mode, userData]);

  const validateForm = () => {
    const requiredFields = ["provider_id", "min_amt", "max_amt"];
    let newErrors = {};
    requiredFields.forEach((field) => {
      const value = document.getElementById(field)?.value.trim();
      if (!value) newErrors[field] = `${field.replace("_", " ")} is required.`;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showMainToast("Please correct the errors before saving.", "danger");
      return;
    }

    const formData = new FormData();
    formData.append("provider_id", document.getElementById("provider_id").value);
    formData.append("user_id",     document.getElementById("user_id").value);
    formData.append("min_amt",     document.getElementById("min_amt").value.trim());
    formData.append("max_amt",     document.getElementById("max_amt").value.trim());
    formData.append("optional",    document.getElementById("optional").value.trim());
    formData.append("id",          userData?.id || "");
    formData.append("action",      "ADDUpdate");
    formData.append("userid",      userid);

    try {
      const response = await fetch("https://pro.mokshiq.in/user_based_operator_action", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: formData,
      });
      const result = await response.json();
      if (result.status) {
        showMainToast(result.data || "Success!", "success");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        showMainToast(result.data || "Something went wrong!", "danger");
      }
    } catch {
      showMainToast("Something went wrong!", "danger");
    }
  };

  // ✅ Wait for editData before rendering form in edit mode
  const isReady = mode === "add" || (mode === "edit" && editData !== null);

  return (
    <BaseModal
      show={show}
      onClose={onClose}
      title={mode === "edit" ? "Edit User Based Operator" : "Add User Based Operator"}
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        { label: mode === "edit" ? "Update" : "Save", className: "btn-primary", onClick: handleSave },
      ]}
    >
      <div className="card-body">
        {!isReady ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          // ✅ key forces full re-render when editData changes, so defaultValues apply correctly
          <form key={editData?.id || "new"} className="row g-3 app-form rounded-control">

            {/* Provider */}
            <div className="col-md-6">
              <label className="form-label">Provider</label>
              <select
                id="provider_id"
                className={`form-select ${errors.provider_id ? "is-invalid" : ""}`}
                defaultValue={editData?.provider_id || ""}
              >
                <option value="">-- Select Provider --</option>
                {providerList.map((p) => (
                  <option key={p.id} value={p.id}>{p.Name}</option>
                ))}
              </select>
              {errors.provider_id && <div className="text-danger">{errors.provider_id}</div>}
            </div>

            {/* User */}
            <div className="col-md-6">
              <label className="form-label">User (optional)</label>
              <select
                id="user_id"
                className="form-select"
                defaultValue={editData?.user_id || ""}
              >
                <option value="">-- Select User --</option>
                {userList.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.name}</option>
                ))}
              </select>
            </div>

            {/* Min Amount */}
            <div className="col-md-6">
              <label className="form-label">Min Amount</label>
              <input
                id="min_amt"
                type="number"
                className={`form-control ${errors.min_amt ? "is-invalid" : ""}`}
                defaultValue={editData?.min_amt || ""}
              />
              {errors.min_amt && <div className="text-danger">{errors.min_amt}</div>}
            </div>

            {/* Max Amount */}
            <div className="col-md-6">
              <label className="form-label">Max Amount</label>
              <input
                id="max_amt"
                type="number"
                className={`form-control ${errors.max_amt ? "is-invalid" : ""}`}
                defaultValue={editData?.max_amt || ""}
              />
              {errors.max_amt && <div className="text-danger">{errors.max_amt}</div>}
            </div>

            {/* Optional */}
            <div className="col-md-6">
              <label className="form-label">Optional</label>
              <input
                id="optional"
                className="form-control"
                defaultValue={editData?.optional || ""}
              />
            </div>

          </form>
        )}
      </div>
    </BaseModal>
  );
}